'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import type { DateRange } from 'react-day-picker';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import { SimpleRangeDatePicker } from '@/components/DatePicker/SimpleRangeDatePicker';
import { FillScreen, ListToolbar, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ActivityTypeFilterChip } from './-components/ActivityTypeFilterChip';
import { ActorFilter } from './-components/ActorFilter';
import { usePermissionRoleActivityTable } from './-hooks/useTable';
import { permissionRoleActivityKeyPrefix } from './-lib/i18nSelectors';
import { isPermissionRoleOperationActivityType, SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_staff/settings/permission-and-roles/_list/operation-activity/')({
  validateSearch: zodValidator(SearchSchema),
  component: RouteComponent
});
function RouteComponent() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: permissionRoleActivityKeyPrefix
  });
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    table,
    displayState,
    totalCount
  } = usePermissionRoleActivityTable({
    search,
    onSearchChange: nextSearch => navigate({
      search: nextSearch
    })
  });
  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!search.occurredFrom) return undefined;
    return {
      from: new Date(search.occurredFrom),
      to: search.occurredTo ? new Date(search.occurredTo) : undefined
    };
  }, [search.occurredFrom, search.occurredTo]);
  const handleActivityTypesChange = useCallback((activityTypes: string[] | undefined) => {
    const nextActivityTypes = (activityTypes ?? []).filter(isPermissionRoleOperationActivityType);
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0,
        activityTypes: nextActivityTypes.length > 0 ? nextActivityTypes : undefined
      })
    });
  }, [navigate]);
  const handleDateRangeChange = useCallback((range?: DateRange) => {
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0,
        occurredFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
        occurredTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined
      })
    });
  }, [navigate]);
  const handleActorChange = useCallback((personId: string | undefined) => {
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0,
        actorPersonId: personId
      })
    });
  }, [navigate]);
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <Card className="p-2 shadow-xs">
        <ListToolbar>
          <ActivityTypeFilterChip value={search.activityTypes} onChange={handleActivityTypesChange} />
          <SimpleRangeDatePicker value={dateRange} onChange={handleDateRangeChange} placeholder={t($ => $.filter.date)} allowEmpty />
          <ActorFilter personId={search.actorPersonId} onChange={handleActorChange} />
        </ListToolbar>
      </Card>

      {/* 非同期投影のため直後の変更は数秒遅れる。保持期間も 90 日に限られる。 */}
      <Text variant="pSmall" className="text-neutral-muted-on-fill">
        {t($ => $.retentionNotice)}
      </Text>

      <VStack className="min-h-0 flex-1 gap-4">
        <Card className="min-h-0 flex-1 overflow-hidden shadow-xs">
          {/* 行に遷移先を持たないフィードなので行クリックは無効 */}
          <DataTable table={table} displayState={displayState} onRowClick={null} />
        </Card>

        <DataTablePagination table={table} totalCount={totalCount} />
      </VStack>
    </FillScreen>;
}